import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// ASSETS
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import editIcon from "../../assets/edit_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";

// COMPONENTS
import Loader from "../../components/Loader";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import {
  deleteBankForBank,
  updateBankForBank,
} from "../../services/bank/banksForBank"; // Assuming you have an update service
import ErrorBox from "../../components/ErrorBox";
import TextDiv from "../../components/TextDiv";
import AreYouSureModal from "../../components/AreYouSureModal";

const UpdateForeignBankModal = ({ onClose, bankData, onBankUpdated }) => {
  // HOOKS
  const { t } = useTranslation("general");

  // STATES
  const [isEditing, setIsEditing] = useState(false); // Toggle between view and edit mode
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    country: "",
    address: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState([]);

  // Initialize form data with bank data when component mounts
  useEffect(() => {
    if (bankData) {
      setFormData({
        id: bankData.id || "",
        name: bankData.name || "",
        country: bankData.country || "",
        address: bankData.address || "",
        phoneNumber: bankData.phoneNumber || "",
      });
    }
  }, [bankData]);

  // Handlers
  const validations = () => {
    const newErrors = []; // Local array to track errors

    if (!formData.name.trim()) {
      newErrors.push({ name: t("bankName") + " " + t("required") });
    }
    if (!formData.country.trim()) {
      newErrors.push({ country: t("country") + " " + t("required") });
    }
    if (!formData.address.trim()) {
      newErrors.push({ address: t("address") + " " + t("required") });
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.push({ phoneNumber: t("phoneNumber") + " " + t("required") });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear specific field error when user starts typing
    setErrors((prev) => prev.filter((error) => !error[name]));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setErrors([]); // Clear any existing errors
  };

  const handleSave = async () => {
    setLoadingModal(true);

    if (!validations()) {
      setLoadingModal(false);
      return;
    }

    try {
      // Call your update API service here
      const response = await updateBankForBank(formData.id, {
        name: formData.name,
        country: formData.country,
        address: formData.address,
        phoneNumber: formData.phoneNumber,
      });

      if (response.status === 200) {
        setIsEditing(false);
        onBankUpdated(); // Refresh the parent component data
      }
    } catch (error) {
      console.error("Error updating foreign bank:", error);
      // You might want to show an error message to the user here
    } finally {
      setLoadingModal(false);
    }
  };

  const handleDeleteForeignBank = async () => {
    try {
      setLoadingModal(true);

      const response = await deleteBankForBank(formData.id);

      if (response.status === 200) {
        setShowDeleteConfirmation(false);
        onClose();
        onBankUpdated();
      }
    } catch (error) {
      console.error("Error deleting foreign bank:", error);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original bank data
    if (bankData) {
      setFormData({
        id: bankData.id || "",
        name: bankData.name || "",
        country: bankData.country || "",
        address: bankData.address || "",
        phoneNumber: bankData.phoneNumber || "",
      });
    }
    setIsEditing(false);
    setErrors([]);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEditing ? t("editForeignBank") : t("bankDetails")}
      footer={
        <>
          {loadingModal && <Loader />}
          <section className="edit-user-actions">
            {!isEditing ? (
              <>
                <CustomButton
                  onClick={handleEdit}
                  variant={"blank"}
                  title={t("edit")}
                  icon={editIcon}
                  iconSize={16}
                  disabled={loadingModal}
                />
                <CustomButton
                  title={t("delete")}
                  variant="red"
                  icon={deleteIcon}
                  iconSize={20}
                  onClick={() => setShowDeleteConfirmation(true)}
                />
                <CustomButton
                  title={t("close")}
                  variant="blank"
                  icon={closeIcon}
                  iconSize={14}
                  onClick={onClose}
                />
              </>
            ) : (
              <>
                <CustomButton
                  onClick={handleSave}
                  title={t("save")}
                  icon={saveIcon}
                  iconSize={16}
                  disabled={loadingModal}
                />
                <CustomButton
                  title={t("cancel")}
                  variant="blank"
                  icon={closeIcon}
                  iconSize={14}
                  onClick={handleCancel}
                />
              </>
            )}
          </section>
        </>
      }
    >
      {errors.length > 0 && <ErrorBox>{t("emptyInputFields")}</ErrorBox>}

      <div>
        {!isEditing ? (
          <TextDiv
            title="bankName"
            value={formData.name}
            divClasses="o-column-row-switcher"
            pClasses="t-bold_semi-bold u-width--min-width_200"
          />
        ) : (
          <>
            <label>{t("bankName")}:</label>
            <CustomInput
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              errorText={errors.find((err) => err.name)?.name}
              disabled={loadingModal}
            />
          </>
        )}
      </div>

      <div>
        {!isEditing ? (
          <TextDiv
            title="country"
            value={formData.country}
            divClasses="o-column-row-switcher"
            pClasses="t-bold_semi-bold u-width--min-width_200"
          />
        ) : (
          <>
            <label>{t("country")}:</label>
            <CustomInput
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              errorText={errors.find((err) => err.country)?.country}
              disabled={loadingModal}
            />
          </>
        )}
      </div>

      <div>
        {!isEditing ? (
          <TextDiv
            title="address"
            value={formData.address}
            divClasses="o-column-row-switcher"
            pClasses="t-bold_semi-bold u-width--min-width_200"
          />
        ) : (
          <>
            <label>{t("address")}:</label>

            <CustomInput
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              errorText={errors.find((err) => err.address)?.address}
              disabled={loadingModal}
            />
          </>
        )}
      </div>

      <div>
        {!isEditing ? (
          <TextDiv
            title="phoneNumber"
            value={formData.phoneNumber}
            divClasses="o-column-row-switcher"
            pClasses="t-bold_semi-bold u-width--min-width_200"
          />
        ) : (
          <>
            <label>{t("phoneNumber")}:</label>
            <CustomInput
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              errorText={errors.find((err) => err.phoneNumber)?.phoneNumber}
              disabled={loadingModal}
            />
          </>
        )}
      </div>

      {showDeleteConfirmation && (
        <AreYouSureModal
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={() => handleDeleteForeignBank()}
          customMessage={t("deleteBankSureMessage")}
          closeTitle="cancel"
          confirmTitle="confirm"
          loading={loadingModal}
          cancel
        />
      )}
    </Modal>
  );
};

export default UpdateForeignBankModal;
